/**
=========================================================
* Soft UI Dashboard React - v4.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Bar } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// VerticalBarChart configurations
import configs from "examples/Charts/BarCharts/VerticalBarChart/configs";

// Soft UI Dashboard React base styles
import colors from "assets/theme/base/colors";

function VerticalBarChart({ title, description, height, chart, showLegend, headerActions }) {
  const chartDatasets = chart.datasets
    ? chart.datasets.map((dataset) => ({
        ...dataset,
        weight: 5,
        borderWidth: 0,
        borderRadius: 4,
        backgroundColor: colors[dataset.color]
          ? colors[dataset.color || "dark"].main
          : colors.dark.main,
        fill: false,
        maxBarThickness: 35,
      }))
    : [];

  const base = configs(chart.labels || [], chartDatasets);
  const { data, options } = {
    ...base,
    options: {
      ...base.options,
      plugins: {
        ...base.options.plugins,
        legend: {
          ...(base.options.plugins?.legend || {}),
          display: Boolean(showLegend),
          position: "bottom",
        },
      },
    },
  };

  const renderChart = (
    <SoftBox p={2}>
      {title || description ? (
        <SoftBox px={description ? 1 : 0} pt={description ? 1 : 0}>
          {title && (
            <SoftBox
              mb={description ? 1 : 0}
              display="flex"
              alignItems="flex-start"
              justifyContent="space-between"
              gap={1}
              flexWrap="wrap"
            >
              <SoftTypography variant="h6" sx={{ flex: "1 1 160px", minWidth: 0 }}>
                {title}
              </SoftTypography>
              {headerActions ? (
                <SoftBox sx={{ flexShrink: 0, ml: { xs: 0, sm: "auto" } }}>{headerActions}</SoftBox>
              ) : null}
            </SoftBox>
          )}
          {description ? (
            <SoftBox mb={2}>
              <SoftTypography component="div" variant="button" fontWeight="regular" color="text">
                {description}
              </SoftTypography>
            </SoftBox>
          ) : null}
        </SoftBox>
      ) : null}
      {useMemo(
        () => (
          <SoftBox height={height}>
            <Bar data={data} options={options} />
          </SoftBox>
        ),
        [chart, height, showLegend]
      )}
    </SoftBox>
  );

  return title || description ? <Card>{renderChart}</Card> : renderChart;
}

// Setting default values for the props of VerticalBarChart
VerticalBarChart.defaultProps = {
  title: "",
  description: "",
  height: "19.125rem",
  showLegend: false,
  headerActions: null,
};

// Typechecking props for the VerticalBarChart
VerticalBarChart.propTypes = {
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.array).isRequired,
  showLegend: PropTypes.bool,
  headerActions: PropTypes.node,
};

export default VerticalBarChart;
